import React, { useState } from "react";
import ReactCardFlip from "react-card-flip";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

import { CardFlipProps, Partner, Speaker } from "src/library/types/types";
import {
  customButtonStyle,
  zIndexes,
  backgroundColorIsDark,
} from "src/library/styles/theme";

export default function ConnectathonCardFlip(props: CardFlipProps) {
  const { partner, speaker, sectionBackGroundColor } = props;
  const [isFlipped, setFlipped] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  // Set default colours to card that is on a purple background where the Entain pattern is not visible.
  const [cardColors, setCardCardColours] = useState({
    frontCardTitleColor: "text.light",
    frontCardTitleBackgroundColor: "common.black",
    backCardBackgroundColor: "common.black",
    backCardTitleColor: "text.light",
    backCardTextColor: "text",
  });
  const [data, setData] = useState<Partner | Speaker>({
    name: "",
    about: "",
    image: "",
    link: "",
    invertLogoColor: false,
    bgColor: "",
  });

  React.useEffect(() => {
    if (speaker !== undefined) {
      setIsSpeaker(true);
      setData(speaker);
    } else if (partner !== undefined) {
      setData(partner);
    }

    if (backgroundColorIsDark(sectionBackGroundColor)) {
      setCardCardColours({
        frontCardTitleColor: "text.light",
        frontCardTitleBackgroundColor: "background.primary",
        backCardBackgroundColor: "background.primary",
        backCardTitleColor: "text.light",
        backCardTextColor: "text",
      });
    }
  }, [partner, speaker, sectionBackGroundColor]);

  function handleSubmit(e: any) {
    e.preventDefault();
    setFlipped(!isFlipped);
  }

  function handleLinkClick(e: any) {
    e.preventDefault();
    e.stopPropagation();
    window.open(data.link, "_blank", "noopener,noreferrer");
  }

  return (
    <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
      {/* FRONT */}
      <Card
        className="card"
        onClick={handleSubmit}
        sx={{
          height: "415px",
          minWidth: "310px",
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          boxShadow: "none",
          position: "relative",
          cursor: "pointer",
          bgcolor: data.bgColor,
        }}
      >
        <CardMedia
          component="img"
          image={data.image}
          alt={`${isSpeaker ? "guest-speaker" : "company-partner"}-${data.name}`}
          sx={{
            width: "100%",
            objectFit: "contain",
            filter: data.invertLogoColor ? "invert()" : "",
            zIndex: 1,
          }}
        />
        <CardContent
          sx={{
            textAlign: "left",
            width: "100%",
            height: "auto",
            position: "absolute",
            bgcolor: cardColors.frontCardTitleBackgroundColor,
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Typography
            gutterBottom
            variant="h4"
            component="p"
            color={cardColors.frontCardTitleColor}
            fontWeight="fontWeightExtraBold"
          >
            {data.name}
          </Typography>
          <CardActions sx={{ p: 0, mt: 2 }}>
            <Button
              size="large"
              color="secondary"
              variant="contained"
              sx={customButtonStyle("small")}
              component="button"
            >
              Learn More <KeyboardArrowRightIcon />
            </Button>
          </CardActions>
        </CardContent>
      </Card>
      {/* BACK */}
      <Card
        className="card"
        onClick={handleSubmit}
        sx={{
          height: "415px",
          minWidth: "310px",
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          boxShadow: "none",
          mx: 1,
          position: "relative",
          cursor: "pointer",
        }}
      >
        <CardContent
          sx={{
            textAlign: "left",
            bgcolor: cardColors.backCardBackgroundColor,
            width: "100%",
            height: "100%",
            position: "absolute",
            display: "flex",
          }}
        >
          <Grid container direction="column" justifyContent="space-between">
            <Grid item>
              <Grid item container justifyContent="space-between">
                <Typography
                  variant="h5"
                  maxWidth="220px"
                  component="p"
                  color={cardColors.backCardTitleColor}
                  fontWeight="fontWeightExtraBold"
                >
                  {data.name}
                </Typography>
                <IconButton
                  color="secondary"
                  onClick={handleSubmit}
                  sx={{ zIndex: zIndexes.Above }}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
              <Typography
                paddingTop="5px"
                maxHeight="265px"
                variant="body2"
                component="p"
                color={cardColors.backCardTextColor}
                fontWeight="fontWeight"
              >
                {data.about}
              </Typography>
            </Grid>
            {data.link && (
              <Grid item alignItems="flex-end">
                <Button
                  size="large"
                  color="secondary"
                  variant="contained"
                  sx={customButtonStyle("small")}
                  component="button"
                  onClick={handleLinkClick}
                  href={data.link}
                >
                  {isSpeaker ? "Read More" : "Visit Partner"} <KeyboardArrowRightIcon />
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </ReactCardFlip>
  );
}
